// apps/web/src/features/crm/leads/activity/useLeadFileUpload.ts
//
// Shared upload hook for lead file attachments (#1809). Extracts the 3-step
// flow (validate → presigned PUT → bind metadata row) so `LeadFileDrawer` and
// the card's `ArtifactsBlock` (#2316, formerly `LeadFilesSection.UploadControl`)
// share a single implementation.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LEAD_FILE_ALLOWED_MIME, LEAD_FILE_NAME_MAX, type LeadFileMime } from '@magimp/shared';
import { useCreateLeadFile } from '@magimp/shared-hooks';
import { requestUploadUrl } from '../../../../api/requests/index.js';
import { formatSize } from '../../../../i18n/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { errorMessage } from '../../../../api/errors.js';

export function isSupportedMime(value: string): value is LeadFileMime {
  return (LEAD_FILE_ALLOWED_MIME as readonly string[]).includes(value);
}

export interface UseLeadFileUploadOpts {
  maxBytes: number;
  onUploaded?: () => void;
}

export function useLeadFileUpload(
  leadId: string,
  opts: UseLeadFileUploadOpts,
): { upload: (file: File) => Promise<void>; uploading: boolean } {
  const { maxBytes, onUploaded } = opts;
  const { t } = useTranslation(['leads']);
  const showToast = useToastStore((s) => s.show);
  const create = useCreateLeadFile(leadId);
  const [uploading, setUploading] = useState(false);

  async function upload(file: File): Promise<void> {
    if (!isSupportedMime(file.type)) {
      showToast({ tone: 'error', text: t('leads:activity.files.errors.unsupportedMime') });
      return;
    }
    if (file.size > maxBytes) {
      showToast({
        tone: 'error',
        text: t('leads:activity.files.errors.tooLarge', { size: formatSize(maxBytes) }),
      });
      return;
    }
    if (file.name.length > LEAD_FILE_NAME_MAX) {
      showToast({
        tone: 'error',
        text: t('leads:activity.files.errors.nameTooLong', { max: LEAD_FILE_NAME_MAX }),
      });
      return;
    }
    setUploading(true);
    try {
      const signed = await requestUploadUrl({
        contentType: file.type,
        contentLength: file.size,
        scope: 'lead-file',
      });
      const put = await fetch(signed.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'content-type': file.type },
      });
      if (!put.ok) throw new Error(`upload failed: ${put.status}`);
      await new Promise<void>((resolve, reject) => {
        create.mutate(
          {
            objectKey: signed.objectKey,
            fileName: file.name,
            contentType: file.type as LeadFileMime,
            contentLength: file.size,
          },
          {
            onSuccess: () => {
              showToast({ tone: 'success', text: t('leads:activity.files.toasts.uploaded') });
              onUploaded?.();
              resolve();
            },
            onError: (err) => {
              showToast({ tone: 'error', text: errorMessage(err) });
              reject(err instanceof Error ? err : new Error(String(err)));
            },
          },
        );
      });
    } catch (err) {
      // Toast already shown for mutation errors; surface upload-only failures here.
      if (err instanceof Error && err.message.startsWith('upload failed')) {
        showToast({ tone: 'error', text: errorMessage(err) });
      }
    } finally {
      setUploading(false);
    }
  }

  return { upload, uploading };
}
